import React, { useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { money, num } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileText, AlertTriangle, CheckCircle2, Info, Loader2, Plus, Trash2, Download, ExternalLink, Upload, Lock } from "lucide-react";
import { ClaudeBadge, when } from "@/components/claude/ClaudeBadge";
import { uploadToBid } from "@/lib/chunkedUpload";

const docUrl = (id) => `${API_BASE}/documents/${id}/download?inline=true`;

export function MoneyCheck({ mc }) {
  if (!mc) return null;
  if (!mc.has_total) {
    return <div data-testid="proposal-money-nofigures" className="flex gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2"><Info className="w-4 h-4 shrink-0" />No total was given with this proposal, so GridForge can't check it against the bid total. Add the total when you upload it.</div>;
  }
  if (mc.mismatch) {
    return (
      <div data-testid="proposal-money-mismatch" className="flex gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>{mc.explain || <>The uploaded proposal says <b>{money(mc.provided_total)}</b> and the live bid total is <b>{money(mc.live_total)}</b>. Send bid will ask someone to confirm before it goes out.</>}</span>
      </div>
    );
  }
  return <div data-testid="proposal-money-match" className="flex gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2"><CheckCircle2 className="w-4 h-4 shrink-0" />The uploaded total {money(mc.provided_total)} matches the live bid total.</div>;
}

function Figures({ f }) {
  const cells = [["Total", f.total != null ? money(f.total) : null], ["$/W", f.cost_per_watt != null ? `$${Number(f.cost_per_watt).toFixed(4)}` : null],
    ["MW", f.capacity_mw != null ? num(f.capacity_mw) : null], ["Start", f.start], ["Finish", f.finish]].filter(([, v]) => v);
  return (
    <div data-testid="proposal-provided-figures">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {cells.map(([k, v]) => (
          <div key={k} className="rounded-md border border-slate-100 px-3 py-2"><div className="text-[10px] uppercase tracking-wide text-slate-400">{k}</div><div className="text-sm font-mono text-slate-800">{v}</div></div>
        ))}
      </div>
      {f.sections?.length > 0 && (
        <table className="w-full text-sm mt-3"><tbody>
          {f.sections.map((s, i) => <tr key={i} className="border-b border-slate-50"><td className="py-1.5 text-slate-700">{s.name}</td><td className="text-right font-mono text-slate-800">{s.amount != null ? money(s.amount) : "—"}</td></tr>)}
        </tbody></table>
      )}
    </div>
  );
}

function History({ rows }) {
  return (
    <div data-testid="proposal-provided-history">
      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Earlier uploads (kept in the documents)</div>
      {rows.map((h) => (
        <a key={h.document_id + h.at} href={docUrl(h.document_id)} target="_blank" rel="noreferrer"
          className="flex items-center gap-2 text-xs text-slate-600 hover:text-[#003E87] py-1"><FileText className="w-3.5 h-3.5" />{h.label} · {h.filename} · {when(h.at)}</a>
      ))}
    </div>
  );
}

export function ProvidedView({ info }) {
  const p = info.provided;
  return (
    <Card className="p-6 space-y-4" data-testid="proposal-provided-view">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-lg bg-[#003E87]/10 text-[#003E87] grid place-items-center shrink-0"><FileText className="w-5 h-5" /></div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900" data-testid="proposal-provided-label">{p.label}</div>
          <div className="text-xs text-slate-500 truncate">{p.filename} · uploaded by {p.provided_by_name} {when(p.at)}</div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {info.source === "provided" && <span className="text-[10px] font-semibold uppercase rounded px-1.5 py-0.5 bg-[#F4C400]/30 text-black" data-testid="proposal-provided-main">Main proposal</span>}
          {p.locked && <span className="text-[10px] font-semibold uppercase rounded px-1.5 py-0.5 bg-slate-900 text-white inline-flex items-center gap-1"><Lock className="w-3 h-3" />Locked</span>}
          {p.via === "claude" && <ClaudeBadge label="Uploaded via Claude" />}
        </div>
      </div>
      {info.source === "provided" && <MoneyCheck mc={info.money_check} />}
      {p.figures && <Figures f={p.figures} />}
      <iframe title="Uploaded proposal" src={docUrl(p.document_id)} data-testid="proposal-provided-frame" className="w-full h-[75vh] rounded-lg border border-slate-200 bg-slate-50" />
      {info.provided_history?.length > 0 && <History rows={info.provided_history} />}
    </Card>
  );
}

export function ProvidedActions({ bid, provided }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/bids/${bid.bid_id}/proposal/pdf`, { responseType: "blob", timeout: 120000 });
      const m = /filename="?([^";]+)"?/i.exec(res.headers["content-disposition"] || "");
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : provided.filename || "Proposal.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("Could not download the PDF"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" data-testid="proposal-provided-open" onClick={() => window.open(docUrl(provided.document_id), "_blank")}><ExternalLink className="w-4 h-4 mr-1.5" /> Open / Print</Button>
      <Button variant="outline" data-testid="proposal-provided-download" disabled={busy} onClick={download}>
        {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Download PDF
      </Button>
    </>
  );
}

export function SourceSwitch({ info, view, setView, canSwitch, onMakeMain }) {
  return (
    <div className="mb-4 flex items-center gap-2 flex-wrap">
      <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-white" data-testid="proposal-source-switch">
        {[["provided", "Uploaded PDF"], ["generated", "Built in GridForge"]].map(([k, l]) => (
          <button key={k} data-testid={`proposal-view-${k}`} onClick={() => setView(k)}
            className={`px-3 py-1 text-xs rounded-md transition-colors ${view === k ? "bg-[#003E87] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            {l}{info.source === k ? " · main" : ""}
          </button>
        ))}
      </div>
      {view !== info.source && canSwitch && (
        <Button size="sm" variant="outline" data-testid="proposal-make-main" onClick={() => onMakeMain(view)}>Make this the main proposal</Button>
      )}
    </div>
  );
}

export function GenerateChoiceDialog({ open, onOpenChange, onChoose }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="proposal-generate-choice">
        <DialogHeader>
          <DialogTitle>Which proposal should be the main one?</DialogTitle>
          <DialogDescription>This bid has an uploaded proposal. Generating builds a new one from the estimate; the uploaded PDF stays in the documents either way.</DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" data-testid="proposal-generate-keep-provided" onClick={() => onChoose(false)}>Keep the uploaded PDF as main</Button>
          <Button data-testid="proposal-generate-make-main" onClick={() => onChoose(true)} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">Make the generated one main</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_FIG = { total: "", cost_per_watt: "", capacity_mw: "", start: "", finish: "" };

function FigureFields({ fig, setFig, sections, setSections }) {
  const set = (k) => (e) => setFig({ ...fig, [k]: e.target.value });
  const setSec = (i, k, v) => setSections(sections.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-slate-700">Figures in this proposal <span className="font-normal text-slate-400">(GridForge checks the total against the bid)</span></div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[["total", "Total $"], ["cost_per_watt", "$/W"], ["capacity_mw", "MW"], ["start", "Start"], ["finish", "Finish"]].map(([k, l]) => (
          <label key={k} className="text-[11px] text-slate-500">{l}<Input data-testid={`proposal-upload-fig-${k}`} value={fig[k]} onChange={set(k)} className="h-8 text-sm mt-0.5" /></label>
        ))}
      </div>
      {sections.map((s, i) => (
        <div key={i} className="flex gap-2">
          <Input placeholder="Section" value={s.name} onChange={(e) => setSec(i, "name", e.target.value)} className="h-8 text-sm" data-testid={`proposal-upload-sec-name-${i}`} />
          <Input placeholder="Amount" value={s.amount} onChange={(e) => setSec(i, "amount", e.target.value)} className="h-8 text-sm w-36" data-testid={`proposal-upload-sec-amount-${i}`} />
          <button onClick={() => setSections(sections.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button data-testid="proposal-upload-add-section" onClick={() => setSections([...sections, { name: "", amount: "" }])} className="text-xs text-[#003E87] inline-flex items-center gap-1 hover:underline"><Plus className="w-3 h-3" /> Add a section amount</button>
    </div>
  );
}

const cleanFig = (fig, sections) => {
  const out = {};
  Object.entries(fig).forEach(([k, v]) => { if (String(v).trim()) out[k] = ["start", "finish"].includes(k) ? v.trim() : Number(String(v).replace(/[$,\s]/g, "")); });
  const secs = sections.filter((s) => s.name.trim()).map((s) => ({ name: s.name.trim(), amount: String(s.amount).trim() ? Number(String(s.amount).replace(/[$,\s]/g, "")) : null }));
  if (secs.length) out.sections = secs;
  return Object.keys(out).length ? out : null;
};

export function UploadProposalDialog({ bid, open, onOpenChange, onDone, hasGenerated }) {
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("Proposal");
  const [fig, setFig] = useState({ ...EMPTY_FIG, capacity_mw: bid.capacity_mw ?? "" });
  const [sections, setSections] = useState([]);
  const [makeMain, setMakeMain] = useState(true);
  const [busy, setBusy] = useState("");
  const submit = async () => {
    if (!file) { toast.error("Choose the proposal PDF"); return; }
    try {
      setBusy("Uploading…");
      const docs = await uploadToBid(bid.bid_id, file, { onState: (s) => setBusy(s.status === "processing" ? "Filing…" : `Uploading ${s.pct}%`) });
      if (!docs.length) throw new Error("The file didn't come through. Try again.");
      setBusy("Saving…");
      const r = await api.post(`/bids/${bid.bid_id}/proposal/provided`, { document_id: docs[0].document_id, label, figures: cleanFig(fig, sections), make_main: makeMain });
      toast.success(r.data.source === "provided" ? "Uploaded proposal is now the main proposal" : "Proposal uploaded");
      onDone(r.data);
      onOpenChange(false);
      setFile(null);
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Upload failed"); }
    finally { setBusy(""); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="proposal-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-4 h-4 text-[#003E87]" /> Upload your own proposal</DialogTitle>
          <DialogDescription>The PDF becomes the bid's proposal: the Proposal tab, PDF download, share link, e-sign page, proposal email and Send bid all use it. Earlier versions stay in the documents.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input type="file" accept="application/pdf,.pdf" data-testid="proposal-upload-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <label className="text-xs text-slate-500 block">Label<Input data-testid="proposal-upload-label" value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 text-sm mt-0.5" placeholder="Proposal R0, BAFO …" /></label>
          <FigureFields fig={fig} setFig={setFig} sections={sections} setSections={setSections} />
          {hasGenerated && (
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="proposal-upload-make-main" checked={makeMain} onChange={(e) => setMakeMain(e.target.checked)} />Make it the main proposal</label>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={!!busy}>Cancel</Button>
          <Button data-testid="proposal-upload-submit" onClick={submit} disabled={!!busy} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />{busy}</> : "Upload proposal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
